type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'silent';

type ConsoleMethod = 'debug' | 'info' | 'log' | 'warn' | 'error';

const levelPriority: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
  silent: 100,
};

const methodLevel: Record<ConsoleMethod, LogLevel> = {
  debug: 'debug',
  info: 'info',
  log: 'info',
  warn: 'warn',
  error: 'error',
};

const tokenPatterns: RegExp[] = [
  /(Bearer\s+)[A-Za-z0-9._-]+/g,
  /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g,
];

function redactString(value: string): string {
  return tokenPatterns.reduce((acc, pattern) => {
    if (pattern.source.startsWith('(Bearer')) {
      return acc.replace(pattern, '$1[REDACTED]');
    }
    return acc.replace(pattern, '[JWT_REDACTED]');
  }, value);
}

function sanitizeArg(arg: unknown): unknown {
  if (typeof arg === 'string') {
    return redactString(arg);
  }

  if (arg && typeof arg === 'object') {
    try {
      return JSON.parse(redactString(JSON.stringify(arg)));
    } catch {
      return arg;
    }
  }

  return arg;
}

function normalizeLevel(rawLevel: string | undefined, isProduction: boolean): LogLevel {
  const defaultLevel: LogLevel = isProduction ? 'warn' : 'debug';
  const level = (rawLevel || defaultLevel).toLowerCase() as LogLevel;

  if (level in levelPriority) {
    return level;
  }

  return defaultLevel;
}

declare global {
  interface Window {
    __consoleGuardInstalled__?: boolean;
  }
}

export function installConsoleGuard(): void {
  if (window.__consoleGuardInstalled__) return;
  window.__consoleGuardInstalled__ = true;

  const isProduction = process.env.NODE_ENV === 'production';
  const minLevel = normalizeLevel(process.env.REACT_APP_LOG_LEVEL, isProduction);
  const minPriority = levelPriority[minLevel];

  const methods: ConsoleMethod[] = ['debug', 'info', 'log', 'warn', 'error'];

  for (const method of methods) {
    const original = console[method].bind(console);

    console[method] = (...args: unknown[]) => {
      const messageLevel = methodLevel[method];
      if (levelPriority[messageLevel] < minPriority) {
        return;
      }

      const sanitizedArgs = args.map(sanitizeArg);
      original(...sanitizedArgs);
    };
  }
}
